import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken, logoutSession } from '../utils/authSession';
import { apiGet } from '../api/client';

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAccessToken();
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/admin/login');
      return;
    }

    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      const data = await apiGet('/api/admin/dashboard');
      setStats(data.statistics);
      setRecentActivity(data.recentActivity);
    } catch (error) {
      if (error.response?.status === 401) {
        await logoutSession({ redirect: false });
        navigate('/admin/login');
      } else {
        console.error('Error fetching dashboard data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading-state">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading dashboard...</p>
        </div>
      </AdminLayout>
    );
  }

  const cards = [
    {
      label: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: 'bi-people',
      tone: 'blue'
    },
    {
      label: 'Active Users',
      value: stats?.activeUsers || 0,
      icon: 'bi-person-check',
      tone: 'teal'
    },
    {
      label: 'Total Posts',
      value: stats?.totalPosts || 0,
      icon: 'bi-newspaper',
      tone: 'navy'
    },
    {
      label: 'Admins',
      value: stats?.usersByRole?.admin || 0,
      icon: 'bi-shield-check',
      tone: 'slate'
    }
  ];

  return (
    <AdminLayout>
      <div className="container-fluid admin-page py-4">
        <div className="admin-page-intro mb-4">
          <p className="admin-page-intro-text mb-0">
            Overview of users, content, and recent system activity.
          </p>
          <div className="admin-quick-links">
            <Link to="/admin/news" className="admin-quick-link">
              <i className="bi bi-plus-lg" aria-hidden="true" />
              New post
            </Link>
            <Link to="/admin/gallery" className="admin-quick-link">
              <i className="bi bi-images" aria-hidden="true" />
              Gallery
            </Link>
            <Link to="/admin/security" className="admin-quick-link">
              <i className="bi bi-shield-lock" aria-hidden="true" />
              Security
            </Link>
          </div>
        </div>

        <div className="row g-3 mb-4">
          {cards.map((card) => (
            <div key={card.label} className="col-6 col-xl-3">
              <div className={`admin-stat-card admin-stat-card--${card.tone}`}>
                <div className="admin-stat-card-icon" aria-hidden="true">
                  <i className={`bi ${card.icon}`} />
                </div>
                <div className="admin-stat-card-body">
                  <span className="admin-stat-card-label">{card.label}</span>
                  <strong className="admin-stat-card-value">{card.value}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="admin-panel-card">
          <div className="admin-panel-card-header">
            <div>
              <h2 className="admin-panel-card-title mb-0">Recent Activity</h2>
              <p className="admin-panel-card-subtitle mb-0">Latest actions across the administration panel</p>
            </div>
            <Link to="/admin/activity-logs" className="admin-panel-link">
              View all
            </Link>
          </div>
          <div className="admin-panel-card-body">
            {recentActivity.length === 0 ? (
              <div className="admin-empty-state">
                <i className="bi bi-clock-history" aria-hidden="true" />
                <p className="mb-0">No recent activity yet.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table admin-table mb-0">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Action</th>
                      <th>Entity</th>
                      <th>Description</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.map((activity) => (
                      <tr key={activity.id}>
                        <td data-label="User">{activity.username || 'System'}</td>
                        <td data-label="Action">
                          <span className="admin-badge">{activity.action}</span>
                        </td>
                        <td data-label="Entity">{activity.entity_type}</td>
                        <td data-label="Description">{activity.description}</td>
                        <td data-label="Time">
                          {new Date(activity.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminDashboard;
